import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { cellValue, splitDateTime, viewColumns } from '@nexus/core';
import { setRowValue } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { cn } from '../../lib/cn';
import { openRow } from '../../lib/peek';
import { Calendar } from '../calendar/Calendar';
import { Card, type ViewProps } from './views';

/** The date property a calendar uses: the view's choice, else the first date. */
const dateProperty = ({ schema, view }: Pick<ViewProps, 'schema' | 'view'>) =>
  schema.properties.find((p) => p.id === view.dateBy) ??
  schema.properties.find((p) => p.type === 'date');

/** Rows placed on their date, by day, week or month; drag a row to another day (or hour). */
export function CalendarView({ schema, view, rows, add, readOnly }: ViewProps) {
  const { t } = useTranslation();
  const store = useStore();
  const def = dateProperty({ schema, view });
  if (!def) return <p className="py-6 text-center text-sm text-fg-muted">{t('db.chooseDate')}</p>;

  const items = rows.flatMap((row) => {
    const dt = splitDateTime(String(cellValue(def, row) ?? ''));
    return dt ? [{ id: row.id, date: dt.date, time: dt.time ?? null, row }] : [];
  });
  return (
    <Calendar
      place={`db:${view.id}`}
      headingLevel={3}
      items={items}
      onAdd={
        readOnly ? undefined : (day, time) => void add({ [def.id]: time ? `${day}T${time}` : day })
      }
      onDrop={
        readOnly
          ? undefined
          : (id, day, time) => {
              const item = items.find((i) => i.id === id);
              if (!item) return;
              const keep = time ?? item.time;
              if (item.date === day && item.time === keep) return;
              void setRowValue(store, id, def, keep ? `${day}T${keep}` : day);
            }
      }
      renderItem={({ row, time }, compact, mode) => (
        <button
          type="button"
          onClick={(e) => openRow(row.id, e)}
          data-testid="db-calendar-item"
          className={cn(
            'block w-full rounded-md bg-bg-active px-1.5 py-0.5 text-left hover:bg-bg-hover',
            compact ? 'truncate' : 'break-words',
            compact ? 'mb-0.5 text-xs' : 'py-1 text-sm',
          )}
        >
          {time && mode !== 'day' && <span className="mr-1 tabular-nums opacity-80">{time}</span>}
          {row.title || t('page.untitled')}
        </button>
      )}
    />
  );
}

/** Cards in a grid, with the view's visible properties. */
export function GalleryView({ schema, view, rows, add, readOnly }: ViewProps) {
  const { t } = useTranslation();
  const columns = viewColumns(schema, view);
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-2">
      {rows.map((row) => (
        <div key={row.id} data-testid="db-card">
          <Card row={row} columns={columns} max={8} tall />
        </div>
      ))}
      {!readOnly && (
        <button
          type="button"
          onClick={() => void add()}
          className="flex min-h-24 items-center justify-center gap-1 rounded-md border border-dashed border-border text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <Plus className="size-4" />
          {t('db.newRow')}
        </button>
      )}
    </div>
  );
}
